// src/components/customer/CustomerAppointmentStatusChart.js
import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { NoDataChart } from './NoDataChart';

// Integration instructions for CustomerDetail.js:
// 
// 1. Add import:
// import CustomerAppointmentStatusChart from '../components/customer/CustomerAppointmentStatusChart';
// 
// 2. Add component after AppointmentHistory (around line 200):
// {/* Customer Appointment Status Chart */}
// <CustomerAppointmentStatusChart 
//   appointments={customerAppointments}
//   loading={appointmentsLoading}
// />

const CustomerAppointmentStatusChart = ({ appointments = [], loading = false }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('allTime');
    const [hiddenStatuses, setHiddenStatuses] = useState(new Set());

    // Status configuration
    const statusConfig = {
        completed: { label: 'הושלם', color: '#3B82F6', order: 1 },
        scheduled: { label: 'מתוכנן', color: '#10B981', order: 2 },
        cancelled: { label: 'בוטל', color: '#F59E0B', order: 3 },
        pending_approval: { label: 'ממתין לאישור', color: '#EAB308', order: 4 },
        no_show_justified: { label: 'לא הגיע - מוצדק', color: '#EC4899', order: 5 },
        no_show: { label: 'לא הגיע', color: '#EF4444', order: 6 }
    };

    // Time period options
    const timePeriods = [
        { key: '3months', label: '3 חודשים' },
        { key: '6months', label: '6 חודשים' },
        { key: '1year', label: 'שנה' },
        { key: 'allTime', label: 'כל הזמן' }
    ];

    // Calculate date ranges
    const getDateRange = useMemo(() => {
        if (!appointments.length) return { start: null, end: null };

        const now = new Date();
        const appointmentDates = appointments.map(apt => new Date(apt.date));
        const minDate = new Date(Math.min(...appointmentDates));
        const maxDate = new Date(Math.max(...appointmentDates));

        switch (selectedPeriod) {
            case '3months':
                const threeMonthsAgo = new Date(now);
                threeMonthsAgo.setMonth(now.getMonth() - 3);
                return { start: threeMonthsAgo, end: now };
            case '6months':
                const sixMonthsAgo = new Date(now);
                sixMonthsAgo.setMonth(now.getMonth() - 6);
                return { start: sixMonthsAgo, end: now };
            case '1year':
                const oneYearAgo = new Date(now);
                oneYearAgo.setFullYear(now.getFullYear() - 1);
                return { start: oneYearAgo, end: now };
            case 'allTime':
            default:
                return { start: minDate, end: maxDate };
        }
    }, [appointments, selectedPeriod]);

    // Process chart data
    const chartData = useMemo(() => {
        if (!appointments.length || !getDateRange.start) return [];

        // Filter appointments by date range
        const filteredAppointments = appointments.filter(apt => {
            const aptDate = new Date(apt.date);
            return aptDate >= getDateRange.start && aptDate <= getDateRange.end;
        });

        // Group by month
        const monthlyData = {};

        filteredAppointments.forEach(apt => {
            const date = new Date(apt.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    month: monthKey,
                    monthLabel: date.toLocaleDateString('he-IL', { year: 'numeric', month: 'short' }),
                    completed: 0,
                    scheduled: 0,
                    cancelled: 0,
                    pending_approval: 0,
                    no_show_justified: 0,
                    no_show: 0
                };
            }

            const status = apt.status || 'scheduled';
            if (monthlyData[monthKey][status] !== undefined) {
                monthlyData[monthKey][status]++;
            }
        });

        return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    }, [appointments, getDateRange]);

    // Calculate summary stats
    const summaryStats = useMemo(() => {
        const stats = {
            completed: 0,
            scheduled: 0,
            cancelled: 0,
            pending_approval: 0,
            no_show_justified: 0,
            no_show: 0
        };

        chartData.forEach(monthData => {
            Object.keys(stats).forEach(status => {
                if (!hiddenStatuses.has(status)) {
                    stats[status] += monthData[status] || 0;
                }
            });
        });

        return stats;
    }, [chartData, hiddenStatuses]);

    // Toggle status visibility
    const toggleStatus = (status) => {
        const newHidden = new Set(hiddenStatuses);
        if (newHidden.has(status)) {
            newHidden.delete(status);
        } else {
            newHidden.add(status);
        }
        setHiddenStatuses(newHidden);
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;

        const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

        return (
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg p-3 shadow-lg">
                <p className="font-medium text-gray-900 mb-2 text-right">{label}</p>
                <div className="space-y-1">
                    {payload
                        .filter(entry => entry.value > 0)
                        .sort((a, b) => (statusConfig[b.dataKey]?.order || 0) - (statusConfig[a.dataKey]?.order || 0))
                        .map(entry => (
                            <div key={entry.dataKey} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">{entry.value}</span>
                                <div className="flex items-center gap-2">
                                    <span>{statusConfig[entry.dataKey]?.label || entry.dataKey}</span>
                                    <div
                                        className="w-3 h-3 rounded-sm"
                                        style={{ backgroundColor: entry.color }}
                                    />
                                </div>
                            </div>
                        ))
                    }
                    <div className="border-t pt-1 mt-1 flex justify-between items-center text-sm font-medium">
                        <span>{total}</span>
                        <span>סה"כ</span>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                <div className="flex items-center gap-2 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">התפלגות סטטוס פגישות</h2>
                </div>
                <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-64 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    if (!appointments.length) {
        return (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
                <div className="flex items-center gap-2 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">התפלגות סטטוס פגישות</h2>
                </div>
                <NoDataChart
                    primaryMessage='אין נתונים להצגה'
                    secondaryMessage='לא נמצאו פגישות ללקוח זה'
                    dataType='appointments'
                />
            </div>
        );
    }

    return (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Header */}
            <div className="flex items-center gap-2 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">התפלגות סטטוס פגישות</h2>
            </div>

            {/* Time Period Selection */}
            <div className="flex gap-2 mb-6 flex-wrap">
                {timePeriods.map(period => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedPeriod === period.key
                                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                            }`}
                    >
                        {period.label}
                    </button>
                ))}
            </div>

            {/* Status Toggle Buttons */}
            <div className="flex gap-2 mb-6 flex-wrap">
                {Object.entries(statusConfig)
                    .sort(([, a], [, b]) => a.order - b.order)
                    .map(([status, config]) => (
                        <button
                            key={status}
                            onClick={() => toggleStatus(status)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${hiddenStatuses.has(status)
                                    ? 'bg-gray-100 text-gray-400 opacity-50'
                                    : 'bg-white border shadow-sm hover:shadow-md'
                                }`}
                        >
                            <div
                                className="w-3 h-3 rounded-sm"
                                style={{ backgroundColor: hiddenStatuses.has(status) ? '#D1D5DB' : config.color }}
                            />
                            <span>{config.label}</span>
                            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                {summaryStats[status]}
                            </span>
                        </button>
                    ))
                }
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-blue-600 mb-1">הושלמו</p>
                    <p className="text-2xl font-bold text-blue-700">{summaryStats.completed}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-red-600 mb-1">לא הגיעו</p>
                    <p className="text-2xl font-bold text-red-700">
                        {summaryStats.no_show + summaryStats.no_show_justified}
                    </p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-green-600 mb-1">סה"כ פגישות</p>
                    <p className="text-2xl font-bold text-green-700">
                        {Object.values(summaryStats).reduce((sum, val) => sum + val, 0)}
                    </p>
                </div>
            </div>

            {/* Chart */}
            {chartData.length > 0 ? (
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis
                                dataKey="monthLabel"
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => value.toString()}
                            />
                            <Tooltip content={<CustomTooltip />} />

                            {/* Render bars in stack order */}
                            {Object.entries(statusConfig)
                                .sort(([, a], [, b]) => a.order - b.order)
                                .map(([status, config]) => (
                                    !hiddenStatuses.has(status) && (
                                        <Bar
                                            key={status}
                                            dataKey={status}
                                            stackId="status"
                                            fill={config.color}
                                            radius={status === 'no_show' ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                        />
                                    )
                                ))
                            }
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <NoDataChart />
            )}
        </div>
    );
};

export default CustomerAppointmentStatusChart;